package se.mittpass.web.pages

import se.mittpass.web.*
import se.mittpass.web.components.*
import se.mittpass.web.pages.*
import se.mittpass.web.viewmodel.*
import se.mittpass.web.style.*

import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*
import se.mittpass.shared.api.*
import se.mittpass.shared.translation.Dictionary

private val analyticsScope = MainScope()

private fun absenceTypeLabel(type: String, dict: Dictionary): String {
    return when (type) {
        "sick" -> dict.absTypeSick
        "vab" -> dict.absTypeVab
        "vacation" -> dict.absTypeVacation
        "leave" -> dict.absTypeLeave
        else -> type
    }
}

private fun dayOfWeekLabel(dateStr: String, dict: Dictionary): String {
    val d = kotlin.js.Date("${dateStr}T12:00:00")
    val dow = ((d.getDay().toInt() + 6) % 7) + 1
    return when (dow) {
        1 -> dict.dayMon
        2 -> dict.dayTue
        3 -> dict.dayWed
        4 -> dict.dayThu
        5 -> dict.dayFri
        6 -> dict.daySat
        7 -> dict.daySun
        else -> ""
    }
}

private fun roundOne(d: Double): String {
    val v = (d * 10).toInt() / 10.0
    return if (v == v.toLong().toDouble()) "${v.toLong()}.0" else "$v"
}

@Composable
fun AnalyticsPage(workplaceId: Long) {
    val dict = LocaleManager.dictionary
    var analytics by remember { mutableStateOf<AnalyticsDto?>(null) }
    var loading by remember { mutableStateOf(true) }
    var activeModal by remember { mutableStateOf<String?>(null) }
    var hoveredBar by remember { mutableStateOf<Int?>(null) }

    LaunchedEffect(workplaceId) {
        analyticsRepo.getAnalytics(workplaceId).onSuccess { analytics = it }
        loading = false
    }

    if (loading) {
        Div({
            style {
                property("text-align", "center")
                property("padding", "4rem 2rem")
                property("color", "var(--rp-pine)")
                property("font-weight", "700")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "center")
                property("gap", "0.75rem")
            }
        }) {
            FaIcon("fa-solid fa-spinner fa-spin", size = "1.2rem", color = "var(--rp-pine)")
            Span { Text(dict.loading) }
        }
        return
    }

    val data = analytics ?: return

    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "1.25rem") } }) {

        // ── Header Bar ──
        Div({
            style {
                property("background", "var(--rp-surface)")
                property("border-radius", "16px")
                property("border", "1px solid var(--rp-hl-med)")
                property("padding", "1rem 1.25rem")
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("flex-wrap", "wrap")
                property("gap", "0.75rem")
            }
        }) {
            Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.85rem") } }) {
                Div({
                    style {
                        property("width", "40px")
                        property("height", "40px")
                        property("border-radius", "12px")
                        property("background", "var(--rp-info-bg)")
                        property("color", "var(--rp-pine)")
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                    }
                }) {
                    FaIcon("fa-solid fa-chart-pie", size = "1.1rem", color = "var(--rp-pine)")
                }
                Div {
                    H2({
                        style {
                            property("margin", "0")
                            property("font-size", "1.25rem")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                        }
                    }) { Text("Analys & Rapporter") }
                    P({
                        style {
                            property("margin", "0.15rem 0 0 0")
                            property("font-size", "0.82rem")
                            property("color", "var(--rp-subtle)")
                        }
                    }) { Text("Verksamhetsöversikt, arbetstidsstatistik och frånvarouppföljning") }
                }
            }

            // Live status badge
            Div({
                style {
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("gap", "0.5rem")
                    property("background", "var(--rp-overlay)")
                    property("border", "1px solid var(--rp-hl-med)")
                    property("padding", "0.4rem 0.85rem")
                    property("border-radius", "9999px")
                    property("font-size", "0.75rem")
                    property("font-weight", "600")
                    property("color", "var(--rp-subtle)")
                }
            }) {
                Div({
                    style {
                        property("width", "8px")
                        property("height", "8px")
                        property("border-radius", "50%")
                        property("background", if (data.currentlyCheckedIn > 0) "var(--rp-pine)" else "var(--rp-muted)")
                    }
                }) {}
                Text(if (data.currentlyCheckedIn > 0) "${data.currentlyCheckedIn} incheckade just nu" else "Realtid synkad")
            }
        }

        // ── Section 1: Verksamhet i realtid & Bemanning ──
        Div({
            style {
                property("display", "grid")
                property("grid-template-columns", "repeat(auto-fit, minmax(240px, 1fr))")
                property("gap", "1rem")
            }
        }) {
            ModernKpiCard(
                category = "BEMANNING",
                label = dict.kpiStaff,
                value = "${data.totalStaff}",
                subtext = "Registrerade medarbetare",
                icon = "fa-users",
                iconBg = "var(--rp-info-bg)",
                iconColor = "var(--rp-pine)",
                onClick = { activeModal = "staff" }
            )

            ModernKpiCard(
                category = "STATUS JUST NU",
                label = dict.kpiCheckedIn,
                value = "${data.currentlyCheckedIn}",
                subtext = if (data.currentlyCheckedIn > 0) "Aktivt i tjänst just nu" else "Ingen aktiv just nu",
                icon = "fa-user-check",
                iconBg = if (data.currentlyCheckedIn > 0) "var(--rp-ok-bg)" else "var(--rp-overlay)",
                iconColor = if (data.currentlyCheckedIn > 0) "var(--rp-ok-text)" else "var(--rp-subtle)",
                badgeText = if (data.currentlyCheckedIn > 0) "AKTIV" else null,
                badgeBg = "var(--rp-ok-bg)",
                badgeColor = "var(--rp-ok-text)",
                onClick = { activeModal = "checkedin" }
            )

            ModernKpiCard(
                category = "SCHEMA DENNA VECKA",
                label = dict.kpiShiftsThisWeek,
                value = "${data.slotsThisWeek}",
                subtext = "Schemalagda arbetspass",
                icon = "fa-calendar-week",
                iconBg = "var(--rp-info-bg)",
                iconColor = "var(--rp-pine)",
                onClick = { activeModal = "slots" }
            )

            ModernKpiCard(
                category = "VAKANSER",
                label = dict.kpiUnclaimedShifts,
                value = "${data.unclaimedSlotsThisWeek}",
                subtext = if (data.unclaimedSlotsThisWeek > 0) "Pass saknar tilldelad personal" else "Alla pass är tillsatta",
                icon = if (data.unclaimedSlotsThisWeek > 0) "fa-triangle-exclamation" else "fa-circle-check",
                iconBg = if (data.unclaimedSlotsThisWeek > 0) "var(--rp-err-bg)" else "var(--rp-ok-bg)",
                iconColor = if (data.unclaimedSlotsThisWeek > 0) "var(--rp-err-text)" else "var(--rp-ok-text)",
                badgeText = if (data.unclaimedSlotsThisWeek > 0) "ÅTGÄRD KRÄVS" else "KLART",
                badgeBg = if (data.unclaimedSlotsThisWeek > 0) "var(--rp-err-bg)" else "var(--rp-ok-bg)",
                badgeColor = if (data.unclaimedSlotsThisWeek > 0) "var(--rp-err-text)" else "var(--rp-ok-text)",
                isAlert = data.unclaimedSlotsThisWeek > 0,
                onClick = { activeModal = "unclaimed" }
            )
        }

        // ── Section 2: Arbetade timmar & Lön & Frånvaro ──
        Div({
            style {
                property("display", "grid")
                property("grid-template-columns", "repeat(auto-fit, minmax(240px, 1fr))")
                property("gap", "1rem")
            }
        }) {
            ModernKpiCard(
                category = "ARBETSTID",
                label = dict.kpiWorkedHours,
                value = "${roundOne(data.totalWorkedHours)}h",
                subtext = "Loggade timmar senaste 30d",
                icon = "fa-clock",
                iconBg = "var(--rp-info-bg)",
                iconColor = "var(--rp-pine)",
                onClick = { activeModal = "worked" }
            )

            ModernKpiCard(
                category = "LÖNEUNDERLAG",
                label = dict.kpiApprovedPayroll,
                value = "${roundOne(data.totalApprovedPayrollHours)}h",
                subtext = "Attesterade och klara för export",
                icon = "fa-file-invoice-dollar",
                iconBg = "var(--rp-ok-bg)",
                iconColor = "var(--rp-ok-text)",
                onClick = { activeModal = "payroll" }
            )

            ModernKpiCard(
                category = "FRÅNVAROANSÖKNINGAR",
                label = dict.kpiAbsencesToReview,
                value = "${data.pendingAbsenceRequests}",
                subtext = if (data.pendingAbsenceRequests > 0) "Väntar på chefsgodkännande" else "Inga väntande ansökningar",
                icon = "fa-calendar-minus",
                iconBg = if (data.pendingAbsenceRequests > 0) "var(--rp-warn-bg)" else "var(--rp-overlay)",
                iconColor = if (data.pendingAbsenceRequests > 0) "var(--rp-warn-text)" else "var(--rp-subtle)",
                badgeText = if (data.pendingAbsenceRequests > 0) "GRANSKA" else null,
                badgeBg = "var(--rp-warn-bg)",
                badgeColor = "var(--rp-warn-text)",
                onClick = { activeModal = "pending_absence" }
            )

            ModernKpiCard(
                category = "FRÅNVARO IDAG",
                label = dict.kpiActiveAbsences,
                value = "${data.activeAbsences}",
                subtext = "Personal med aktiv frånvaro idag",
                icon = "fa-user-slash",
                iconBg = if (data.activeAbsences > 0) "var(--rp-info-bg)" else "var(--rp-overlay)",
                iconColor = if (data.activeAbsences > 0) "var(--rp-iris)" else "var(--rp-subtle)",
                onClick = { activeModal = "active_absence" }
            )
        }

        // ── Primary Chart: Arbetade timmar per dag (14 dagar) ──
        val periodTotalHours = remember(data.dailyHours) { data.dailyHours.sumOf { it.hours } }
        val periodAvgHours = remember(data.dailyHours) {
            if (data.dailyHours.isNotEmpty()) periodTotalHours / data.dailyHours.size else 0.0
        }

        Div({
            style {
                property("background", "var(--rp-surface)")
                property("border-radius", "16px")
                property("border", "1px solid var(--rp-hl-med)")
                property("padding", "1.5rem 1.75rem")
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "1.25rem")
            }
        }) {
            // Chart header
            Div({
                style {
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "space-between")
                    property("flex-wrap", "wrap")
                    property("gap", "0.75rem")
                }
            }) {
                Div {
                    H3({
                        style {
                            property("margin", "0")
                            property("font-size", "1.1rem")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.6rem")
                        }
                    }) {
                        FaIcon("fa-solid fa-chart-column", size = "1rem", color = "var(--rp-pine)")
                        Text(dict.chartWorkedHoursTitle)
                    }
                    P({
                        style {
                            property("margin", "0.25rem 0 0 0")
                            property("font-size", "0.8rem")
                            property("color", "var(--rp-subtle)")
                        }
                    }) { Text("Daglig arbetstid och medarbetarfördelning för de senaste två veckorna") }
                }

                // Summary chips
                Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem"); property("flex-wrap", "wrap") } }) {
                    Div({
                        style {
                            property("background", "var(--rp-info-bg)")
                            property("color", "var(--rp-pine)")
                            property("padding", "0.35rem 0.75rem")
                            property("border-radius", "8px")
                            property("font-size", "0.8rem")
                            property("font-weight", "700")
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.4rem")
                        }
                    }) {
                        FaIcon("fa-solid fa-calculator", size = "0.75rem", color = "var(--rp-pine)")
                        Text("Totalt: ${roundOne(periodTotalHours)}h")
                    }
                    Div({
                        style {
                            property("background", "var(--rp-overlay)")
                            property("border", "1px solid var(--rp-hl-med)")
                            property("color", "var(--rp-subtle)")
                            property("padding", "0.35rem 0.75rem")
                            property("border-radius", "8px")
                            property("font-size", "0.8rem")
                            property("font-weight", "600")
                            property("display", "flex")
                            property("align-items", "center")
                            property("gap", "0.4rem")
                        }
                    }) {
                        FaIcon("fa-solid fa-chart-line", size = "0.75rem", color = "var(--rp-subtle)")
                        Text("Snitt: ${roundOne(periodAvgHours)}h / dag")
                    }
                }
            }

            if (data.dailyHours.isEmpty()) {
                Div({
                    style {
                        property("text-align", "center")
                        property("padding", "3rem 1rem")
                        property("color", "var(--rp-muted)")
                        property("font-size", "0.9rem")
                    }
                }) { Text(dict.noData) }
            } else {
                val maxHours = data.dailyHours.maxOf { it.hours }.coerceAtLeast(1.0)

                Div({
                    style {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", "0.5rem")
                        property("padding-top", "1rem")
                    }
                }) {
                    // Bars container
                    Div({
                        style {
                            property("display", "flex")
                            property("align-items", "flex-end")
                            property("gap", "8px")
                            property("height", "220px")
                            property("border-bottom", "1px solid var(--rp-hl-med)")
                            property("padding-bottom", "4px")
                            property("position", "relative")
                        }
                    }) {
                        data.dailyHours.forEachIndexed { index, entry ->
                            val heightPct = (entry.hours / maxHours * 100).coerceAtLeast(3.0)
                            val isHovered = hoveredBar == index

                            Div({
                                onMouseEnter { hoveredBar = index }
                                onMouseLeave { hoveredBar = null }
                                style {
                                    property("flex", "1")
                                    property("display", "flex")
                                    property("flex-direction", "column")
                                    property("align-items", "center")
                                    property("justify-content", "flex-end")
                                    property("height", "100%")
                                    property("position", "relative")
                                    property("cursor", "pointer")
                                }
                            }) {
                                // Tooltip
                                if (isHovered && entry.persons.isNotEmpty()) {
                                    Div({
                                        style {
                                            property("position", "absolute")
                                            property("bottom", "calc(${heightPct}% + 36px)")
                                            property("background", "var(--rp-pine)")
                                            property("color", "white")
                                            property("padding", "0.85rem 1.1rem")
                                            property("border-radius", "12px")
                                            property("font-size", "0.78rem")
                                            property("white-space", "nowrap")
                                            property("z-index", "50")
                                            property("pointer-events", "none")
                                            property("min-width", "190px")
                                            property("box-shadow", "0 12px 28px rgba(30, 27, 75, 0.25)")
                                            property("transform", "translateX(-50%)")
                                            property("left", "50%")
                                            property("border", "1px solid rgba(255,255,255,0.1)")
                                        }
                                    }) {
                                        Div({
                                            style {
                                                property("font-weight", "800")
                                                property("margin-bottom", "0.45rem")
                                                property("font-size", "0.85rem")
                                                property("display", "flex")
                                                property("align-items", "center")
                                                property("justify-content", "space-between")
                                                property("border-bottom", "1px solid rgba(255,255,255,0.15)")
                                                property("padding-bottom", "0.35rem")
                                            }
                                        }) {
                                            Span { Text("${dayOfWeekLabel(entry.date, dict)} ${entry.date.takeLast(5)}") }
                                            Span({ style { property("color", "var(--rp-info-border)"); property("font-weight", "800") } }) {
                                                Text("${roundOne(entry.hours)}h")
                                            }
                                        }
                                        entry.persons.forEach { p ->
                                            Div({
                                                style {
                                                    property("display", "flex")
                                                    property("justify-content", "space-between")
                                                    property("gap", "1rem")
                                                    property("padding", "0.15rem 0")
                                                    property("font-size", "0.75rem")
                                                }
                                            }) {
                                                Span({ style { property("opacity", "0.85") } }) { Text(p.name) }
                                                Span({ style { property("font-weight", "700"); property("color", "var(--rp-info-bg)") } }) {
                                                    Text("${roundOne(p.hours)}h")
                                                }
                                            }
                                        }
                                        // Arrow
                                        Div({
                                            style {
                                                property("position", "absolute")
                                                property("bottom", "-6px")
                                                property("left", "50%")
                                                property("transform", "translateX(-50%)")
                                                property("width", "0")
                                                property("height", "0")
                                                property("border-left", "6px solid transparent")
                                                property("border-right", "6px solid transparent")
                                                property("border-top", "6px solid var(--rp-text)")
                                            }
                                        }) {}
                                    }
                                }

                                // Top hour badge over bar
                                if (entry.hours > 0) {
                                    Span({
                                        style {
                                            property("font-size", "0.7rem")
                                            property("font-weight", "700")
                                            property("color", if (isHovered) "var(--rp-pine)" else "var(--rp-text)")
                                            property("margin-bottom", "0.35rem")
                                            property("transition", "color 0.15s ease")
                                        }
                                    }) {
                                        Text(roundOne(entry.hours))
                                    }
                                }

                                // Bar body
                                Div({
                                    style {
                                        property("width", "100%")
                                        property("max-width", "36px")
                                        property("height", "${heightPct}%")
                                        property("min-height", "6px")
                                        property("border-radius", "6px 6px 0 0")
                                        property("transition", "all 0.15s ease")
                                        if (isHovered) {
                                            property("background", "var(--rp-pine)")
                                            property("transform", "scaleX(1.08)")
                                            property("box-shadow", "0 4px 14px rgba(79, 70, 229, 0.35)")
                                        } else {
                                            property("background", if (entry.hours > 0) "var(--rp-pine)" else "var(--rp-overlay)")
                                        }
                                    }
                                }) {}
                            }
                        }
                    }

                    // X-Axis readable horizontal labels
                    Div({
                        style {
                            property("display", "flex")
                            property("gap", "8px")
                            property("padding-top", "0.4rem")
                        }
                    }) {
                        data.dailyHours.forEachIndexed { index, entry ->
                            val isHovered = hoveredBar == index
                            val dayNumber = entry.date.takeLast(2)
                            val weekday = dayOfWeekLabel(entry.date, dict)

                            Div({
                                style {
                                    property("flex", "1")
                                    property("display", "flex")
                                    property("flex-direction", "column")
                                    property("align-items", "center")
                                    property("justify-content", "center")
                                    property("padding", "0.2rem 0")
                                    property("border-radius", "6px")
                                    if (isHovered) {
                                        property("background", "var(--rp-info-bg)")
                                    }
                                }
                            }) {
                                Span({
                                    style {
                                        property("font-size", "0.68rem")
                                        property("font-weight", "700")
                                        property("color", if (isHovered) "var(--rp-pine)" else "var(--rp-subtle)")
                                    }
                                }) { Text(weekday) }
                                Span({
                                    style {
                                        property("font-size", "0.65rem")
                                        property("font-weight", "600")
                                        property("color", if (isHovered) "var(--rp-text)" else "var(--rp-muted)")
                                    }
                                }) { Text(dayNumber) }
                            }
                        }
                    }
                }
            }
        }

        // ── Secondary Visualizations: Fördelning per medarbetare & Bemanningseffektivitet ──
        Div({
            style {
                property("display", "grid")
                property("grid-template-columns", "repeat(auto-fit, minmax(340px, 1fr))")
                property("gap", "1.25rem")
            }
        }) {
            // Chart 1: Medarbetarfördelning (Topplistan arbetade timmar 30d)
            Div({
                style {
                    property("background", "var(--rp-surface)")
                    property("border-radius", "16px")
                    property("border", "1px solid var(--rp-hl-med)")
                    property("padding", "1.5rem")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "1rem")
                }
            }) {
                Div({
                    style {
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "space-between")
                        property("border-bottom", "1px solid var(--rp-hl-med)")
                        property("padding-bottom", "0.75rem")
                    }
                }) {
                    Div {
                        H4({
                            style {
                                property("margin", "0")
                                property("font-size", "1rem")
                                property("font-weight", "800")
                                property("color", "var(--rp-text)")
                                property("display", "flex")
                                property("align-items", "center")
                                property("gap", "0.5rem")
                            }
                        }) {
                            FaIcon("fa-solid fa-ranking-star", size = "0.95rem", color = "var(--rp-pine)")
                            Text("Arbetade timmar per medarbetare")
                        }
                        P({
                            style {
                                property("margin", "0.2rem 0 0 0")
                                property("font-size", "0.78rem")
                                property("color", "var(--rp-subtle)")
                            }
                        }) { Text("Senaste 30 dagarna fördelat på personal") }
                    }

                    Span({
                        style {
                            property("background", "var(--rp-info-bg)")
                            property("color", "var(--rp-pine)")
                            property("padding", "0.25rem 0.6rem")
                            property("border-radius", "6px")
                            property("font-size", "0.7rem")
                            property("font-weight", "700")
                        }
                    }) { Text("${data.workedHoursPerPerson.size} anställda") }
                }

                if (data.workedHoursPerPerson.isEmpty()) {
                    Div({
                        style {
                            property("text-align", "center")
                            property("padding", "2rem 1rem")
                            property("color", "var(--rp-muted)")
                            property("font-size", "0.85rem")
                        }
                    }) { Text("Inga registrerade timmar senaste 30 dagarna") }
                } else {
                    val maxPersonHours = data.workedHoursPerPerson.maxOfOrNull { it.hours }?.coerceAtLeast(1.0) ?: 1.0
                    val topStaff = data.workedHoursPerPerson.sortedByDescending { it.hours }.take(6)

                    Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.75rem") } }) {
                        topStaff.forEachIndexed { rank, p ->
                            val pct = (p.hours / maxPersonHours * 100).coerceIn(4.0, 100.0)
                            Div({
                                style {
                                    property("display", "flex")
                                    property("flex-direction", "column")
                                    property("gap", "0.3rem")
                                }
                            }) {
                                Div({
                                    style {
                                        property("display", "flex")
                                        property("justify-content", "space-between")
                                        property("align-items", "center")
                                        property("font-size", "0.82rem")
                                    }
                                }) {
                                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.5rem") } }) {
                                        Span({
                                            style {
                                                property("width", "18px")
                                                property("height", "18px")
                                                property("border-radius", "50%")
                                                property("background", if (rank < 3) "var(--rp-info-bg)" else "var(--rp-overlay)")
                                                property("color", if (rank < 3) "var(--rp-pine)" else "var(--rp-subtle)")
                                                property("font-size", "0.65rem")
                                                property("font-weight", "800")
                                                property("display", "flex")
                                                property("align-items", "center")
                                                property("justify-content", "center")
                                            }
                                        }) { Text("${rank + 1}") }
                                        Span({ style { property("font-weight", "700"); property("color", "var(--rp-text)") } }) { Text(p.name) }
                                    }
                                    Span({ style { property("font-weight", "800"); property("color", "var(--rp-pine)") } }) {
                                        Text("${roundOne(p.hours)}h")
                                    }
                                }

                                // Visual bar track
                                Div({
                                    style {
                                        property("width", "100%")
                                        property("height", "8px")
                                        property("background", "var(--rp-overlay)")
                                        property("border-radius", "9999px")
                                        property("overflow", "hidden")
                                    }
                                }) {
                                    Div({
                                        style {
                                            property("width", "$pct%")
                                            property("height", "100%")
                                            property("background", if (rank == 0) "var(--rp-pine)" else if (rank == 1) "var(--rp-pine)" else "var(--rp-pine)")
                                            property("border-radius", "9999px")
                                            property("transition", "width 0.4s ease")
                                        }
                                    }) {}
                                }
                            }
                        }

                        if (data.workedHoursPerPerson.size > 6) {
                            Button({
                                onClick { activeModal = "worked" }
                                style {
                                    property("margin-top", "0.4rem")
                                    property("background", "transparent")
                                    property("border", "none")
                                    property("color", "var(--rp-pine)")
                                    property("font-size", "0.78rem")
                                    property("font-weight", "700")
                                    property("cursor", "pointer")
                                    property("padding", "0.3rem 0")
                                    property("text-align", "left")
                                    property("display", "flex")
                                    property("align-items", "center")
                                    property("gap", "0.4rem")
                                }
                            }) {
                                Text("Visa alla ${data.workedHoursPerPerson.size} anställda")
                                FaIcon("fa-solid fa-arrow-right", size = "0.7rem", color = "var(--rp-pine)")
                            }
                        }
                    }
                }
            }

            // Chart 2: Tillsättningsgrad & Veckoöversikt
            val totalSlots = data.slotsThisWeek
            val filledSlots = (totalSlots - data.unclaimedSlotsThisWeek).coerceAtLeast(0)
            val fillRatePct = if (totalSlots > 0) ((filledSlots.toDouble() / totalSlots) * 100).toInt() else 100

            Div({
                style {
                    property("background", "var(--rp-surface)")
                    property("border-radius", "16px")
                    property("border", "1px solid var(--rp-hl-med)")
                    property("padding", "1.5rem")
                    property("display", "flex")
                    property("flex-direction", "column")
                    property("gap", "1.25rem")
                }
            }) {
                Div({
                    style {
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "space-between")
                        property("border-bottom", "1px solid var(--rp-hl-med)")
                        property("padding-bottom", "0.75rem")
                    }
                }) {
                    Div {
                        H4({
                            style {
                                property("margin", "0")
                                property("font-size", "1rem")
                                property("font-weight", "800")
                                property("color", "var(--rp-text)")
                                property("display", "flex")
                                property("align-items", "center")
                                property("gap", "0.5rem")
                            }
                        }) {
                            FaIcon("fa-solid fa-gauge-high", size = "0.95rem", color = if (fillRatePct == 100) "var(--rp-ok-text)" else "var(--rp-warn-text)")
                            Text("Bemanningsgrad")
                        }
                        P({
                            style {
                                property("margin", "0.2rem 0 0 0")
                                property("font-size", "0.78rem")
                                property("color", "var(--rp-subtle)")
                            }
                        }) { Text("Tillsatta arbetspass denna kalendervecka") }
                    }

                    Span({
                        style {
                            property("background", if (fillRatePct == 100) "var(--rp-ok-bg)" else "var(--rp-warn-bg)")
                            property("color", if (fillRatePct == 100) "var(--rp-ok-text)" else "var(--rp-warn-text)")
                            property("padding", "0.25rem 0.6rem")
                            property("border-radius", "6px")
                            property("font-size", "0.72rem")
                            property("font-weight", "800")
                        }
                    }) { Text("$fillRatePct% tillsatt") }
                }

                // Visual Proportional Progress Bar
                Div({
                    style {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("gap", "0.6rem")
                    }
                }) {
                    Div({
                        style {
                            property("width", "100%")
                            property("height", "16px")
                            property("background", if (data.unclaimedSlotsThisWeek > 0) "var(--rp-err-bg)" else "var(--rp-overlay)")
                            property("border-radius", "9999px")
                            property("overflow", "hidden")
                            property("display", "flex")
                        }
                    }) {
                        Div({
                            style {
                                property("width", "$fillRatePct%")
                                property("height", "100%")
                                property("background", "var(--rp-pine)")
                                property("border-radius", if (fillRatePct == 100) "9999px" else "9999px 0 0 9999px")
                                property("transition", "width 0.4s ease")
                            }
                        }) {}
                    }

                    // Legend / Breakdown metrics
                    Div({
                        style {
                            property("display", "grid")
                            property("grid-template-columns", "repeat(2, 1fr)")
                            property("gap", "0.75rem")
                            property("margin-top", "0.5rem")
                        }
                    }) {
                        Div({
                            style {
                                property("background", "var(--rp-overlay)")
                                property("border", "1px solid var(--rp-hl-med)")
                                property("border-radius", "12px")
                                property("padding", "0.85rem")
                                property("display", "flex")
                                property("align-items", "center")
                                property("gap", "0.75rem")
                            }
                        }) {
                            Div({
                                style {
                                    property("width", "10px")
                                    property("height", "10px")
                                    property("border-radius", "50%")
                                    property("background", "var(--rp-pine)")
                                }
                            }) {}
                            Div {
                                Div({ style { property("font-size", "1.1rem"); property("font-weight", "800"); property("color", "var(--rp-text)") } }) {
                                    Text("$filledSlots")
                                }
                                Div({ style { property("font-size", "0.72rem"); property("font-weight", "600"); property("color", "var(--rp-subtle)") } }) {
                                    Text("Tillsatta pass")
                                }
                            }
                        }

                        Div({
                            style {
                                property("background", if (data.unclaimedSlotsThisWeek > 0) "var(--rp-err-bg)" else "var(--rp-overlay)")
                                property("border", if (data.unclaimedSlotsThisWeek > 0) "1px solid var(--rp-err-border)" else "1px solid var(--rp-hl-med)")
                                property("border-radius", "12px")
                                property("padding", "0.85rem")
                                property("display", "flex")
                                property("align-items", "center")
                                property("gap", "0.75rem")
                            }
                        }) {
                            Div({
                                style {
                                    property("width", "10px")
                                    property("height", "10px")
                                    property("border-radius", "50%")
                                    property("background", if (data.unclaimedSlotsThisWeek > 0) "var(--rp-love)" else "var(--rp-muted)")
                                }
                            }) {}
                            Div {
                                Div({
                                    style {
                                        property("font-size", "1.1rem")
                                        property("font-weight", "800")
                                        property("color", if (data.unclaimedSlotsThisWeek > 0) "var(--rp-err-text)" else "var(--rp-text)")
                                    }
                                }) {
                                    Text("${data.unclaimedSlotsThisWeek}")
                                }
                                Div({ style { property("font-size", "0.72rem"); property("font-weight", "600"); property("color", "var(--rp-subtle)") } }) {
                                    Text("Otagna vakanser")
                                }
                            }
                        }
                    }
                }

                // Attestation status row
                val payrollHours = data.totalApprovedPayrollHours
                val workedHours = data.totalWorkedHours
                val attestationRatio = if (workedHours > 0) ((payrollHours / workedHours) * 100).toInt().coerceAtMost(100) else 100

                Div({
                    style {
                        property("background", "var(--rp-overlay)")
                        property("border-radius", "12px")
                        property("padding", "0.85rem 1rem")
                        property("border", "1px solid var(--rp-hl-med)")
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "space-between")
                    }
                }) {
                    Div({ style { property("display", "flex"); property("align-items", "center"); property("gap", "0.6rem") } }) {
                        FaIcon("fa-solid fa-file-circle-check", size = "0.9rem", color = "var(--rp-ok-text)")
                        Div {
                            Div({ style { property("font-size", "0.82rem"); property("font-weight", "700"); property("color", "var(--rp-text)") } }) {
                                Text("Löneattestationsgrad")
                            }
                            Div({ style { property("font-size", "0.72rem"); property("color", "var(--rp-subtle)") } }) {
                                Text("${roundOne(payrollHours)}h av ${roundOne(workedHours)}h attesterat")
                            }
                        }
                    }

                    Span({
                        style {
                            property("font-size", "0.85rem")
                            property("font-weight", "800")
                            property("color", "var(--rp-ok-text)")
                        }
                    }) { Text("$attestationRatio%") }
                }
            }
        }
    }

    // Detail modals
    activeModal?.let { modalType ->
        DetailModal(
            data = data,
            modalType = modalType,
            onClose = { activeModal = null }
        )
    }
}

@Composable
private fun ModernKpiCard(
    category: String,
    label: String,
    value: String,
    subtext: String,
    icon: String,
    iconBg: String,
    iconColor: String,
    badgeText: String? = null,
    badgeBg: String = "var(--rp-overlay)",
    badgeColor: String = "var(--rp-subtle)",
    isAlert: Boolean = false,
    onClick: () -> Unit
) {
    Div({
        onClick { onClick() }
        style {
            property("background", "var(--rp-surface)")
            property("border-radius", "16px")
            property("padding", "1.25rem")
            property("border", if (isAlert) "1px solid var(--rp-err-border)" else "1px solid var(--rp-hl-med)")
            property("box-shadow", if (isAlert) "0 1px 3px rgba(220, 38, 38, 0.06)" else "0 1px 3px rgba(0, 0, 0, 0.02)")
            property("cursor", "pointer")
            property("transition", "all 0.15s ease")
            property("display", "flex")
            property("flex-direction", "column")
            property("justify-content", "space-between")
            property("gap", "0.85rem")
        }
        attr("onmouseenter", "this.style.transform='translateY(-2px)';this.style.boxShadow='0 8px 24px rgba(0,0,0,0.06)';this.style.borderColor='var(--rp-info-border)'")
        attr("onmouseleave", "this.style.transform='';this.style.boxShadow='${if (isAlert) "0 1px 3px rgba(220, 38, 38, 0.06)" else "0 1px 3px rgba(0, 0, 0, 0.02)"}';this.style.borderColor='${if (isAlert) "var(--rp-err-border)" else "var(--rp-hl-med)"}'")
    }) {
        // Top row: Category tag + Icon badge
        Div({
            style {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
            }
        }) {
            Span({
                style {
                    property("font-size", "0.68rem")
                    property("font-weight", "800")
                    property("color", "var(--rp-subtle)")
                    property("letter-spacing", "0.5px")
                }
            }) { Text(category) }

            Div({
                style {
                    property("width", "36px")
                    property("height", "36px")
                    property("border-radius", "10px")
                    property("background", iconBg)
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                }
            }) {
                FaIcon("fa-solid $icon", size = "0.95rem", color = iconColor)
            }
        }

        // Mid row: Value & label
        Div {
            Div({
                style {
                    property("font-size", "1.75rem")
                    property("font-weight", "800")
                    property("color", "var(--rp-text)")
                    property("line-height", "1.1")
                }
            }) { Text(value) }
            Div({
                style {
                    property("font-size", "0.85rem")
                    property("font-weight", "700")
                    property("color", "var(--rp-text)")
                    property("margin-top", "0.25rem")
                }
            }) { Text(label) }
        }

        // Bottom row: Subtext + Drilldown indicator
        Div({
            style {
                property("display", "flex")
                property("align-items", "center")
                property("justify-content", "space-between")
                property("border-top", "1px solid var(--rp-hl-med)")
                property("padding-top", "0.6rem")
            }
        }) {
            Span({
                style {
                    property("font-size", "0.75rem")
                    property("color", "var(--rp-subtle)")
                    property("font-weight", "500")
                }
            }) { Text(subtext) }

            if (badgeText != null) {
                Span({
                    style {
                        property("background", badgeBg)
                        property("color", badgeColor)
                        property("padding", "0.2rem 0.5rem")
                        property("border-radius", "6px")
                        property("font-size", "0.65rem")
                        property("font-weight", "800")
                        property("letter-spacing", "0.4px")
                    }
                }) { Text(badgeText) }
            } else {
                FaIcon("fa-solid fa-arrow-right", size = "0.75rem", color = "var(--rp-muted)")
            }
        }
    }
}

@Composable
private fun DetailModal(data: AnalyticsDto, modalType: String, onClose: () -> Unit) {
    val dict = LocaleManager.dictionary
    val (title, subtitle) = when (modalType) {
        "staff" -> dict.modalStaffTitle to "${data.totalStaff} ${dict.modalStaffSub}"
        "checkedin" -> dict.modalCheckedInTitle to "${data.currentlyCheckedIn} ${dict.modalCheckedInSub}"
        "worked" -> dict.modalWorkedTitle to "${roundOne(data.totalWorkedHours)}h ${dict.modalWorkedSub}"
        "payroll" -> dict.modalPayrollTitle to "${roundOne(data.totalApprovedPayrollHours)}h ${dict.modalPayrollSub}"
        "slots" -> dict.modalShiftsTitle to "${data.slotsThisWeek} ${dict.modalShiftsSub}"
        "unclaimed" -> dict.modalUnclaimedTitle to "${data.unclaimedSlotsThisWeek} ${dict.modalUnclaimedSub}"
        "pending_absence" -> dict.modalPendingAbsenceTitle to "${data.pendingAbsenceRequests} ${dict.modalPendingAbsenceSub}"
        "active_absence" -> dict.modalActiveAbsenceTitle to "${data.activeAbsences} ${dict.modalActiveAbsenceSub}"
        else -> "" to ""
    }

    Div({
        onClick { onClose() }
        style {
            property("position", "fixed"); property("inset", "0"); property("z-index", "1000")
            property("background", "rgba(15, 23, 42, 0.45)"); property("display", "flex")
            property("align-items", "center"); property("justify-content", "center")
            property("backdrop-filter", "blur(4px)")
            property("padding", "1rem")
        }
    }) {
        Div({
            onClick { it.stopPropagation() }
            style {
                property("background", "var(--rp-surface)"); property("border-radius", "20px")
                property("padding", "1.75rem 2rem"); property("width", "560px"); property("max-width", "95vw")
                property("max-height", "85vh"); property("overflow-y", "auto")
                property("border", "1px solid var(--rp-hl-med)")
                property("box-shadow", "0 20px 48px rgba(15, 23, 42, 0.15)")
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "1rem")
            }
        }) {
            // Header with title and close X button
            Div({
                style {
                    property("display", "flex")
                    property("align-items", "flex-start")
                    property("justify-content", "space-between")
                    property("border-bottom", "1px solid var(--rp-hl-med)")
                    property("padding-bottom", "1rem")
                }
            }) {
                Div {
                    H3({
                        style {
                            property("margin", "0")
                            property("font-size", "1.25rem")
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                        }
                    }) { Text(title) }
                    P({
                        style {
                            property("margin", "0.2rem 0 0 0")
                            property("color", "var(--rp-subtle)")
                            property("font-size", "0.85rem")
                        }
                    }) { Text(subtitle) }
                }

                Button({
                    onClick { onClose() }
                    style {
                        property("background", "var(--rp-overlay)")
                        property("border", "none")
                        property("border-radius", "8px")
                        property("width", "32px")
                        property("height", "32px")
                        property("display", "flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("cursor", "pointer")
                        property("color", "var(--rp-subtle)")
                    }
                }) {
                    FaIcon("fa-solid fa-xmark", size = "0.9rem", color = "var(--rp-subtle)")
                }
            }

            // Modal Body
            Div({ style { property("display", "flex"); property("flex-direction", "column"); property("gap", "0.25rem") } }) {
                when (modalType) {
                    "staff" -> {
                        data.staffDetails.forEach { s ->
                            DetailRow(
                                primary = s.name,
                                secondary = s.email,
                                badge = if (s.role == "manager") dict.roleManager else dict.roleStaff,
                                badgeBg = if (s.role == "manager") "var(--rp-warn-bg)" to "var(--rp-gold)" else "var(--rp-info-bg)" to "var(--rp-pine)"
                            )
                        }
                    }
                    "checkedin" -> {
                        if (data.checkedInDetails.isEmpty()) {
                            EmptyState(dict.emptyCheckedIn)
                        } else {
                            data.checkedInDetails.forEach { c ->
                                DetailRow(
                                    primary = c.name,
                                    secondary = c.email,
                                    badge = "${dict.statusCheckedInSince} ${c.since}",
                                    badgeBg = "var(--rp-ok-bg)" to "var(--rp-pine)"
                                )
                            }
                        }
                    }
                    "worked" -> {
                        if (data.workedHoursPerPerson.isEmpty()) {
                            EmptyState(dict.emptyWorked)
                        } else {
                            val maxH = data.workedHoursPerPerson.maxOfOrNull { it.hours } ?: 1.0
                            data.workedHoursPerPerson.forEach { p ->
                                PersonBarRow(name = p.name, hours = p.hours, maxHours = maxH, color = "var(--rp-pine)")
                            }
                        }
                    }
                    "payroll" -> {
                        if (data.payrollHoursPerPerson.isEmpty()) {
                            EmptyState(dict.emptyPayroll)
                        } else {
                            val maxH = data.payrollHoursPerPerson.maxOfOrNull { it.hours } ?: 1.0
                            data.payrollHoursPerPerson.forEach { p ->
                                PersonBarRow(name = p.name, hours = p.hours, maxHours = maxH, color = "var(--rp-ok-text)")
                            }
                        }
                    }
                    "slots" -> {
                        if (data.slotsThisWeekDetails.isEmpty()) {
                            EmptyState(dict.emptyShifts)
                        } else {
                            data.slotsThisWeekDetails.forEach { s ->
                                DetailRow(
                                    primary = "${dayOfWeekLabel(s.day, dict)} ${s.day.takeLast(5)}  ${s.startTime}–${s.endTime}",
                                    secondary = s.assignedTo ?: "Ingen tilldelad",
                                    badge = if (s.assignedTo != null) dict.statusClaimed else dict.statusUnclaimed,
                                    badgeBg = if (s.assignedTo != null) "var(--rp-ok-bg)" to "var(--rp-pine)" else "var(--rp-err-bg)" to "var(--rp-love)"
                                )
                            }
                        }
                    }
                    "unclaimed" -> {
                        if (data.unclaimedSlotDetails.isEmpty()) {
                            EmptyState(dict.emptyUnclaimed)
                        } else {
                            data.unclaimedSlotDetails.forEach { s ->
                                DetailRow(
                                    primary = "${dayOfWeekLabel(s.day, dict)} ${s.day.takeLast(5)}  ${s.startTime}–${s.endTime}",
                                    secondary = "Ledigt pass som behöver bemannas",
                                    badge = dict.statusUnclaimed,
                                    badgeBg = "var(--rp-err-bg)" to "var(--rp-love)"
                                )
                            }
                        }
                    }
                    "pending_absence" -> {
                        if (data.pendingAbsenceDetails.isEmpty()) {
                            EmptyState(dict.emptyPendingAbsence)
                        } else {
                            data.pendingAbsenceDetails.forEach { a ->
                                val typeLabel = absenceTypeLabel(a.type, dict)
                                DetailRow(
                                    primary = "${a.name}: $typeLabel",
                                    secondary = "${a.startDate} – ${a.endDate}" + (a.note?.let { " ($it)" } ?: ""),
                                    badge = dict.statusPending,
                                    badgeBg = "var(--rp-warn-bg)" to "var(--rp-gold)"
                                )
                            }
                        }
                    }
                    "active_absence" -> {
                        if (data.activeAbsenceDetails.isEmpty()) {
                            EmptyState(dict.emptyActiveAbsence)
                        } else {
                            data.activeAbsenceDetails.forEach { a ->
                                val typeLabel = absenceTypeLabel(a.type, dict)
                                DetailRow(
                                    primary = "${a.name}: $typeLabel",
                                    secondary = "${a.startDate} – ${a.endDate}",
                                    badge = typeLabel,
                                    badgeBg = "var(--rp-info-bg)" to "var(--rp-iris)"
                                )
                            }
                        }
                    }
                }
            }

            // Footer
            Div({
                style {
                    property("display", "flex")
                    property("justify-content", "flex-end")
                    property("border-top", "1px solid var(--rp-hl-med)")
                    property("padding-top", "1rem")
                }
            }) {
                Button({
                    onClick { onClose() }
                    style {
                        property("padding", "0.6rem 1.4rem")
                        property("border", "1px solid var(--rp-hl-med)")
                        property("background", "var(--rp-overlay)")
                        property("color", "var(--rp-text)")
                        property("border-radius", "10px")
                        property("font-weight", "700")
                        property("font-size", "0.875rem")
                        property("cursor", "pointer")
                    }
                }) { Text(dict.close) }
            }
        }
    }
}

@Composable
private fun DetailRow(primary: String, secondary: String?, badge: String, badgeBg: Pair<String, String>) {
    Div({
        style {
            property("display", "flex")
            property("justify-content", "space-between")
            property("align-items", "center")
            property("padding", "0.75rem 0.5rem")
            property("border-bottom", "1px solid var(--rp-hl-med)")
            property("border-radius", "8px")
        }
    }) {
        Div {
            Div({ style { property("font-weight", "700"); property("color", "var(--rp-text)"); property("font-size", "0.875rem") } }) { Text(primary) }
            secondary?.let { sec ->
                Div({ style { property("font-size", "0.78rem"); property("color", "var(--rp-subtle)"); property("margin-top", "0.15rem") } }) { Text(sec) }
            }
        }
        Span({
            style {
                property("background", badgeBg.first)
                property("color", badgeBg.second)
                property("padding", "0.25rem 0.65rem")
                property("border-radius", "6px")
                property("font-size", "0.72rem")
                property("font-weight", "700")
                property("white-space", "nowrap")
            }
        }) { Text(badge) }
    }
}

@Composable
private fun PersonBarRow(name: String, hours: Double, maxHours: Double, color: String) {
    val pct = (hours / maxHours * 100).coerceAtLeast(3.0)
    Div({
        style {
            property("display", "flex")
            property("align-items", "center")
            property("gap", "0.85rem")
            property("padding", "0.6rem 0.5rem")
            property("border-bottom", "1px solid var(--rp-hl-med)")
        }
    }) {
        Div({
            style {
                property("width", "130px")
                property("min-width", "130px")
                property("font-weight", "700")
                property("font-size", "0.85rem")
                property("color", "var(--rp-text)")
            }
        }) { Text(name) }
        Div({ style { property("flex", "1"); property("display", "flex"); property("align-items", "center"); property("gap", "0.6rem") } }) {
            Div({
                style {
                    property("flex", "1")
                    property("height", "14px")
                    property("background", "var(--rp-overlay)")
                    property("border-radius", "6px")
                    property("overflow", "hidden")
                }
            }) {
                Div({
                    style {
                        property("height", "100%")
                        property("width", "$pct%")
                        property("background", color)
                        property("border-radius", "6px")
                        property("transition", "width 0.3s ease")
                    }
                }) {}
            }
            Span({
                style {
                    property("font-weight", "800")
                    property("font-size", "0.8rem")
                    property("color", "var(--rp-text)")
                    property("white-space", "nowrap")
                    property("min-width", "45px")
                    property("text-align", "right")
                }
            }) {
                Text("${roundOne(hours)}h")
            }
        }
    }
}

@Composable
private fun EmptyState(message: String) {
    Div({
        style {
            property("text-align", "center")
            property("padding", "2.5rem 1rem")
            property("color", "var(--rp-muted)")
            property("font-size", "0.875rem")
        }
    }) {
        Text(message)
    }
}
